import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import {
  getCampaignTimeline,
  getVoiceReport,
  getCampaignList,
  executeCampaign,
  pauseCampaign,
  resumeCampaign,
  deleteCampaign,
  downloadCampaignPdf,
} from "../api/CampaignDetailApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, AreaChart, Area
} from "recharts";
import {
  ArrowLeft, Mail, MessageSquare, Phone, Rocket, RefreshCw, Search,
  CheckCircle2, XCircle, Clock, Send, MousePointerClick, AlertTriangle,
  Pause, Play, Trash2, User as UserIcon, X, Download, MessageCircle, ChevronDown, ChevronUp,
  Eye, Paperclip
} from "lucide-react";
import { toast } from "sonner";

const CHANNEL_META = {
  email: { label: "Email", icon: Mail, color: "#8c3add" },
  sms: { label: "SMS", icon: MessageSquare, color: "#1fa7a1" },
  whatsapp: { label: "WhatsApp", icon: Phone, color: "#22c55e" },
  voice: { label: "Voice", icon: Phone, color: "#f97316" },
};

const OUTCOME_META = {
  disclosed_credentials: { label: "Disclosed credentials", color: "#ef4444", bg: "bg-red-50" },
  verified_caller: { label: "Verified caller", color: "#0ea5e9", bg: "bg-sky-50" },
  hung_up: { label: "Hung up", color: "#f59e0b", bg: "bg-amber-50" },
  engaged: { label: "Engaged", color: "#8c3add", bg: "bg-purple-50" },
  neutral: { label: "Neutral", color: "#64748b", bg: "bg-gray-50" },
};

const EVENT_ICON = {
  sent: { Icon: Send, color: "#8c3add" },
  delivered: { Icon: CheckCircle2, color: "#22c55e" },
  opened: { Icon: Mail, color: "#1fa7a1" },
  clicked: { Icon: MousePointerClick, color: "#f59e0b" },
  submitted: { Icon: AlertTriangle, color: "#ef4444" },
  reported: { Icon: CheckCircle2, color: "#0ea5e9" },
  failed: { Icon: XCircle, color: "#ef4444" },
  undelivered: { Icon: XCircle, color: "#ef4444" },
  queued: { Icon: Clock, color: "#94a3b8" },
  sending: { Icon: Clock, color: "#94a3b8" },
  attachment_opened: { Icon: Eye, color: "#06b6d4" },
  attachment_downloaded: { Icon: Paperclip, color: "#6366f1" },
};

const TYPE_COLORS = {
  sent: "#8c3add",
  delivered: "#22c55e",
  opened: "#1fa7a1",
  clicked: "#f59e0b",
  submitted: "#ef4444",
  reported: "#0ea5e9",
  failed: "#dc2626",
  attachment_opened: "#06b6d4",
  attachment_downloaded: "#6366f1",
};

const SEARCH_KEY = "search";

const fmt = (ts) => ts ? new Date(ts).toLocaleString() : "—";

const StatChip = ({ label, value, color, icon: Icon }) => (
  <div className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl">
    <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
      <Icon size={18} style={{ color }} />
    </div>
    <div>
      <p className="text-xl font-bold text-[#1f1f23]">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  </div>
);

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const isFirstSearchRender = useRef(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [compareCampaignId, setCompareCampaignId] = useState("");
  const [compareData, setCompareData] = useState(null);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [voiceReport, setVoiceReport] = useState(null);
  const [expandedTranscript, setExpandedTranscript] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Filtering ab poori tarah backend se hoti hai: search + status dono params me jate hain
  const fetchTimeline = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params[SEARCH_KEY] = debouncedSearch;
      if (statusFilter !== "all") params.status = statusFilter;

      const res = await getCampaignTimeline(id, params);
      setData(res.data);
      // If voice campaign, fetch voice-specific report too
      if ((res.data?.campaign?.simulation_type || "email") === "voice") {
        try {
          const vr = await getVoiceReport(id);
          setVoiceReport(vr.data);
        } catch (e) { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load campaign");
    }
    setLoading(false);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await downloadCampaignPdf(id);
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `campaign-${data?.campaign?.name || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate PDF");
    }
    setDownloadingPdf(false);
  };

  useEffect(() => { fetchTimeline(true); }, [id]);

  // Search ya status badle to backend se dobara data lao
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    fetchTimeline(false);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    getCampaignList().then((r) => setAvailableCampaigns((r.data || []).filter((c) => c.id !== id))).catch(() => { });
  }, [id]);

  useEffect(() => {
    if (!compareCampaignId) { setCompareData(null); return; }
    getCampaignTimeline(compareCampaignId).then((r) => setCompareData(r.data)).catch(() => setCompareData(null));
  }, [compareCampaignId]);

  // debouncedSearch + statusFilter deps me hain taaki auto-refresh purane filter ke bina data na laye
  useEffect(() => {
    if (autoRefresh) {
      timerRef.current = setInterval(() => fetchTimeline(false), 5000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [autoRefresh, id, debouncedSearch, statusFilter]);

  const handleExecute = () => {
    setShowLaunchModal(true);
  };

  // Modal ke "Launch Now" button pe asli kaam
  const confirmExecute = async () => {
    setExecuting(true);
    try {
      await executeCampaign(id);
      toast.success("Launched — sending in progress");
      setTimeout(() => fetchTimeline(false), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to launch");
    }
    setExecuting(false);
    setShowLaunchModal(false);
  };

  const handlePause = async () => {
    try { await pauseCampaign(id); toast.success("Paused"); fetchTimeline(false); }
    catch { toast.error("Failed to pause"); }
  };
  const handleResume = async () => {
    try { await resumeCampaign(id); toast.success("Resumed"); fetchTimeline(false); }
    catch { toast.error("Failed to resume"); }
  };
  const goToList = () => {
    const ch = data?.campaign?.simulation_type || "email";
    navigate(`/campaigns?channel=${ch}`);
  };
  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteCampaign(id);
      toast.success("Deleted");
      goToList();
    } catch {
      toast.error("Failed to delete");
    }
    setDeleting(false);
    setShowDeleteModal(false);
  };

  // ===== Trend chart: activity_graph se hourly grouping, label = us hour ka exact (pehla) event time =====
  // Hooks rule: har render pe chalna chahiye
  const trendData = useMemo(() => {
    const graph = (data?.activity_graph || []).filter((g) => g.time && g.event_type);
    if (graph.length === 0) return [];

    const exact = (ts) => ts.slice(0, 19).replace("T", " "); // "2026-09-25 09:24:14" (response ka hi time)

    const buckets = {};
    const allTypes = new Set();
    [...graph]
      .sort((x, y) => new Date(x.time) - new Date(y.time))
      .forEach((g) => {
        const key = g.time.slice(0, 13); // "2026-09-25T09" -> hour group (floor sirf grouping ke liye)
        if (!buckets[key]) {
          buckets[key] = {
            bucket: key,
            label: exact(g.time).slice(5), // "09-25 09:24:14" -> exact time, floor nahi
            firstTime: exact(g.time),
            lastTime: exact(g.time),
          };
        }
        buckets[key].lastTime = exact(g.time);
        buckets[key][g.event_type] = (buckets[key][g.event_type] || 0) + (g.count || 1);
        allTypes.add(g.event_type);
      });

    const sorted = Object.values(buckets).sort((x, y) => x.bucket.localeCompare(y.bucket));
    sorted.forEach((row) => {
      allTypes.forEach((t) => { if (!(t in row)) row[t] = 0; });
    });
    return sorted;
  }, [data]);

  if (loading) {
    return <CulsightPageLoader />;
  }

  if (!data) return <div className="p-8 text-gray-500">Campaign not found.</div>;

  const { campaign, counts, timeline, recipients } = data;
  const channel = campaign.simulation_type || "email";
  const meta = CHANNEL_META[channel] || CHANNEL_META.email;
  const ChannelIcon = meta.icon;
  const canExecute = ["draft", "scheduled", "paused"].includes(campaign.status);

  // Status filter backend se aa chuka hai — frontend pe sirf data dikhana hai
  const filteredRecipients = Array.isArray(recipients) ? recipients : [];

  // Sirf row-click wala selected recipient filter frontend pe rakha hai (ye status filter nahi hai)
  const filteredTimeline = Array.isArray(timeline)
    ? timeline.filter((e) => !selectedRecipient || e.user_id === selectedRecipient.user_id)
    : [];

  const chartedTypes = Object.keys(TYPE_COLORS).filter(
    (t) => trendData.some((b) => (b[t] || 0) > 0)
  );
  const chartedCompareTypes = Object.keys(TYPE_COLORS).filter(
    (t) => trendData.some((b) => (b[`cmp_${t}`] || 0) > 0)
  );

  // Per-recipient drill-down: build their personal timeline
  const recipientEvents = selectedRecipient
    ? (Array.isArray(timeline) ? timeline : []).filter((e) => e.user_id === selectedRecipient.user_id)
    : [];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={goToList} className="mt-1" data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <ChannelIcon size={20} style={{ color: meta.color }} />
              <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">{campaign.name}</h1>
              <Badge style={{ backgroundColor: meta.color, color: "white" }}>{meta.label}</Badge>
              <Badge variant="outline" className="capitalize">{campaign.status}</Badge>
            </div>
            <p className="text-gray-500 text-sm mt-1">
              Group: <span className="font-medium">{campaign.user_group}</span> • Template: <span className="font-medium">{campaign.email_template_name || "—"}</span>
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setAutoRefresh(!autoRefresh)} className="gap-2" data-testid="auto-refresh-btn">
            <RefreshCw size={14} className={autoRefresh ? "animate-spin" : ""} />
            {autoRefresh ? "Auto-refresh on" : "Auto-refresh off"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloadingPdf} className="gap-2" data-testid="download-pdf-btn">
            <Download size={14} /> {downloadingPdf ? "Preparing…" : "Download PDF"}
          </Button>
          {canExecute && (
            <Button onClick={handleExecute} disabled={executing} className="gap-2 text-white" style={{ backgroundColor: meta.color }} data-testid="detail-launch-btn">
              <Rocket size={14} /> {executing ? "Launching..." : "Launch Now"}
            </Button>
          )}
          {campaign.status === "active" && (
            <Button variant="outline" size="sm" onClick={handlePause} className="gap-2"><Pause size={14} /> Pause</Button>
          )}
          {campaign.status === "paused" && (
            <Button variant="outline" size="sm" onClick={handleResume} className="gap-2"><Play size={14} /> Resume</Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDelete} className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 size={14} /> Delete
          </Button>
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-8 gap-3" data-testid="counts-grid">
        <StatChip label="Sent" value={counts.sent} color="#8c3add" icon={Send} />
        <StatChip label="Delivered" value={counts.delivered} color="#22c55e" icon={CheckCircle2} />
        <StatChip label="Opened" value={counts.opened} color="#1fa7a1" icon={Mail} />
        <StatChip label="Clicked" value={counts.clicked} color="#f59e0b" icon={MousePointerClick} />
        <StatChip label="Submitted" value={counts.submitted} color="#ef4444" icon={AlertTriangle} />
        <StatChip label="Reported" value={counts.reported} color="#0ea5e9" icon={CheckCircle2} />
        <StatChip label="Attachment Downloaded" value={counts.attachment_downloaded || 0} color="#6366f1" icon={Paperclip} />
        <StatChip label="Failed" value={counts.failed} color="#ef4444" icon={XCircle} />
      </div>

      {/* VOICE-SPECIFIC REPORT (only for voice campaigns) */}
      {channel === "voice" && voiceReport && (
        <Card className="border border-[#f97316]/25 shadow-sm" data-testid="voice-report-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Phone size={16} style={{ color: "#f97316" }} />
              Voice Simulation Outcomes
              <Badge variant="outline" className="ml-1">
                {voiceReport.summary?.answered || 0} answered / {voiceReport.summary?.total_called || 0} called
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Outcome tag distribution */}
            {voiceReport.summary?.outcome_tag_counts && Object.keys(voiceReport.summary.outcome_tag_counts).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="voice-outcome-tags">
                {Object.entries(voiceReport.summary.outcome_tag_counts).map(([tag, count]) => {
                  const m = OUTCOME_META[tag] || OUTCOME_META.neutral;
                  return (
                    <span key={tag} className={`text-xs px-3 py-1.5 rounded-full ${m.bg} border`} style={{ color: m.color, borderColor: `${m.color}33` }}>
                      <b>{m.label}</b> · {count}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Per-user conversations */}
            {(voiceReport.transcripts || []).length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">
                No conversations recorded yet. Transcripts appear here once recipients engage with the vishing call.
              </p>
            ) : (
              <div className="space-y-2" data-testid="voice-transcripts-list">
                {voiceReport.transcripts.map((tr) => {
                  const isOpen = expandedTranscript === tr.user_id;
                  return (
                    <div key={tr.user_id} className="border border-gray-100 rounded-lg overflow-hidden" data-testid={`voice-transcript-${tr.user_id}`}>
                      <button
                        type="button"
                        onClick={() => setExpandedTranscript(isOpen ? null : tr.user_id)}
                        className="w-full flex items-center justify-between gap-3 p-3 hover:bg-gray-50 text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="p-2 rounded-full bg-[#f97316]/10">
                            <MessageCircle size={14} className="text-[#f97316]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1f1f23] truncate">{tr.user_name || tr.user_email || "Unknown"}</p>
                            <p className="text-xs text-gray-500 truncate">{tr.phone || tr.user_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          {(tr.outcome_tags || []).map((tag) => {
                            const m = OUTCOME_META[tag] || OUTCOME_META.neutral;
                            return (
                              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full border" style={{ color: m.color, borderColor: `${m.color}55`, backgroundColor: `${m.color}12` }}>
                                {m.label}
                              </span>
                            );
                          })}
                          <span className="text-xs text-gray-500">{tr.turn_count || 0} turns · {tr.duration_seconds || 0}s</span>
                          {isOpen ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="border-t border-gray-100 p-3 space-y-2 bg-gray-50/50" data-testid={`voice-transcript-body-${tr.user_id}`}>
                          {(tr.turns || []).length === 0 ? (
                            <p className="text-xs text-gray-500">No dialogue recorded.</p>
                          ) : (tr.turns || []).map((turn, i) => (
                            <div key={i} className={`flex ${turn.role === "user" ? "justify-end" : "justify-start"}`}>
                              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${turn.role === "user" ? "bg-[#8c3add]/10 text-[#1f1f23]" : "bg-white border border-gray-100"}`}>
                                <p className="text-[10px] font-semibold uppercase mb-0.5" style={{ color: turn.role === "user" ? "#8c3add" : "#1fa7a1" }}>
                                  {turn.role === "user" ? "Recipient" : "AI Agent"}
                                </p>
                                <p className="text-sm">{turn.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Unanswered list */}
            {(voiceReport.unanswered || []).length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Unanswered / no transcript</p>
                <div className="flex flex-wrap gap-2" data-testid="voice-unanswered-list">
                  {voiceReport.unanswered.map((u) => (
                    <span key={u.user_id} className="text-xs px-2 py-1 border rounded-md bg-white text-gray-600">
                      {u.user_name || u.user_email} · {u.phone || "no phone"}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter recipients by name, email, phone..." className="pl-9" data-testid="recipient-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {["all", "sent", "delivered", "opened", "clicked", "submitted", "reported", "attachment_opened", "attachment_downloaded", "failed"].map((st) => (
            <Button
              key={st}
              variant={statusFilter === st ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(st)}
              className={statusFilter === st ? "text-white" : ""}
              style={statusFilter === st ? { backgroundColor: meta.color } : {}}
              data-testid={`filter-${st}`}
            >
              {st === "all" ? "All" : st.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
            </Button>
          ))}
        </div>
      </div>

      {/* Engagement Trend Chart (backend activity_graph se) */}
      {trendData.length > 0 && (
        <Card className="border border-gray-100 shadow-sm" data-testid="trend-chart-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base font-semibold">Engagement Trend (hourly)</CardTitle>
              <div className="flex items-center gap-2">
                {/* <select
                  className="text-xs border border-gray-200 rounded px-2 py-1"
                  value={compareCampaignId}
                  onChange={(e) => setCompareCampaignId(e.target.value)}
                  data-testid="compare-select"
                >
                  <option value="">Compare with…</option>
                  {availableCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>{`${c.name} (${c.simulation_type || "email"})`}</option>
                  ))}
                </select>
                {compareCampaignId && (
                  <Button variant="outline" size="sm" onClick={() => setCompareCampaignId("")} className="h-7 gap-1" data-testid="clear-compare">
                    <X size={12} /> Clear
                  </Button>
                )} */}
                <div className="flex gap-1 flex-wrap">
                  {chartedTypes.map((t) => (
                    <Badge key={t} className="text-xs capitalize" style={{ backgroundColor: `${TYPE_COLORS[t]}15`, color: TYPE_COLORS[t] }}>
                      {t.replace(/_/g, " ")}
                    </Badge>
                  ))}
                  {chartedCompareTypes.length > 0 && (
                    <Badge className="text-xs bg-gray-100 text-gray-600" style={{ borderStyle: "dashed", borderWidth: 1 }}>
                      compare (dashed)
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    {chartedTypes.map((t) => (
                      <linearGradient key={t} id={`grad-${t}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={TYPE_COLORS[t]} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={TYPE_COLORS[t]} stopOpacity={0.05} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
                    labelFormatter={(label, payload) => {
                      const p = payload?.[0]?.payload;
                      if (!p) return label;
                      return p.firstTime === p.lastTime ? p.firstTime : `${p.firstTime} → ${p.lastTime}`;
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {chartedTypes.map((t) => (
                    <Area
                      key={t}
                      type="monotone"
                      dataKey={t}
                      stroke={TYPE_COLORS[t]}
                      fill={`url(#grad-${t})`}
                      strokeWidth={2}
                      dot={trendData.length < 3}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {compareData && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="p-2 rounded bg-gray-50">
                  <p className="text-gray-500">Comparing with</p>
                  <p className="font-medium text-[#1f1f23] truncate">{compareData.campaign?.name}</p>
                </div>
                {["sent", "delivered", "clicked", "failed"].map((k) => {
                  const a = counts[k] || 0;
                  const b = compareData.counts[k] || 0;
                  const delta = a - b;
                  return (
                    <div key={k} className="p-2 rounded bg-gray-50">
                      <p className="text-gray-500 capitalize">{k}</p>
                      <p className="font-medium">{a} vs {b} <span className={delta >= 0 ? "text-green-600" : "text-red-600"}>({delta >= 0 ? "+" : ""}{delta})</span></p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selected Recipient banner */}
      {selectedRecipient && (
        <div className="flex items-center justify-between p-3 rounded-xl border" style={{ backgroundColor: `${meta.color}10`, borderColor: `${meta.color}33` }} data-testid="selected-recipient-banner">
          <div className="flex items-center gap-3">
            <UserIcon size={16} style={{ color: meta.color }} />
            <div>
              <p className="text-sm font-medium" style={{ color: meta.color }}>
                Viewing only events for: <span className="font-semibold">{selectedRecipient.name || selectedRecipient.email}</span>
              </p>
              <p className="text-xs text-gray-500">
                {recipientEvents.length} event{recipientEvents.length === 1 ? "" : "s"} for this recipient
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSelectedRecipient(null)} className="gap-1" data-testid="clear-selection-btn">
            <X size={14} /> Show all
          </Button>
        </div>
      )}

      {/* Two-column: Recipients table | Live timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-3 border border-gray-100 shadow-sm" data-testid="recipients-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center justify-between">
              <span>Recipients ({filteredRecipients.length})</span>
              {channel !== "email" && (
                <span className="text-xs text-gray-500 font-normal">{data.twilio_event_count} Twilio webhook events</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Recipient</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">{channel === "email" ? "Email" : "Phone"}</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Last Status</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecipients.length === 0 ? (
                    <tr><td colSpan={4} className="py-12 text-center text-gray-500 text-sm">No recipients yet — launch the campaign or wait for delivery callbacks.</td></tr>
                  ) : filteredRecipients.map((r) => {
                    const info = EVENT_ICON[r.last_status] || { Icon: Clock, color: "#94a3b8" };
                    const StatusIcon = info.Icon;
                    const isSelected = selectedRecipient && selectedRecipient.user_id === r.user_id;
                    return (
                      <tr
                        key={r.user_id}
                        className="border-t border-gray-50 hover:bg-gray-50/50 cursor-pointer transition-colors"
                        style={isSelected ? { backgroundColor: `${meta.color}12` } : {}}
                        onClick={() => setSelectedRecipient(isSelected ? null : r)}
                        data-testid={`recipient-${r.user_id}`}
                      >
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            {isSelected && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />}
                            <p className="text-sm font-medium text-[#1f1f23]">{r.name || "—"}</p>
                          </div>
                          {r.department && <p className="text-xs text-gray-400">{r.department}</p>}
                        </td>
                        <td className="py-2 px-3 text-sm text-gray-600">{channel === "email" ? r.email : (r.phone || r.email || "—")}</td>
                        <td className="py-2 px-3">
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full capitalize" style={{ backgroundColor: `${info.color}15`, color: info.color }}>
                            <StatusIcon size={12} /> {(r.last_status || "—").replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-xs text-gray-500">{fmt(r.last_timestamp)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border border-gray-100 shadow-sm" data-testid="timeline-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center justify-between">
              <span>Live Activity</span>
              <Badge variant="outline" className="text-xs">{filteredTimeline.length} events</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {filteredTimeline.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-8">No activity yet</p>
              ) : filteredTimeline.map((evt, idx) => {
                const info = EVENT_ICON[evt.type] || { Icon: Clock, color: "#94a3b8" };
                const EvtIcon = info.Icon;
                return (
                  <div key={idx} className="flex gap-3 items-start" data-testid={`event-${idx}`}>
                    <div className="p-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: `${info.color}15` }}>
                      <EvtIcon size={14} style={{ color: info.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm flex items-center gap-2">
                        <span className="font-medium capitalize">{(evt.type || "event").replace(/_/g, " ")}</span>
                        {evt.kind === "twilio" && <Badge variant="outline" className="text-[10px] py-0">Twilio</Badge>}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{evt.user_email || evt.to || evt.user_id || "—"}</p>
                      {evt.message && <p className="text-[11px] text-gray-400 truncate font-mono">{evt.message}</p>}
                      <p className="text-[11px] text-gray-400">{fmt(evt.timestamp)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Launch confirmation modal */}
      <Dialog
        open={showLaunchModal}
        onOpenChange={(open) => {
          if (!executing) setShowLaunchModal(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket size={18} style={{ color: meta.color }} />
              {campaign?.status === "paused" ? "Relaunch Campaign?" : "Launch Campaign?"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-gray-600">
              Are you sure you want to launch{" "}
              <strong>{campaign?.name}</strong> now?
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Messages will start sending to all recipients in the group.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowLaunchModal(false)}
              disabled={executing}
              data-testid="launch-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmExecute}
              disabled={executing}
              className="text-white"
              style={{ backgroundColor: meta.color }}
              data-testid="launch-confirm-btn"
            >
              <Rocket size={16} className="mr-2" />
              {executing ? "Launching..." : "Launch Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation modal */}
      <Dialog
        open={showDeleteModal}
        onOpenChange={(open) => {
          if (!deleting) setShowDeleteModal(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={18} className="text-red-600" />
              Delete Campaign?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-gray-600">
              Are you sure you want to delete{" "}
              <strong>{campaign?.name}</strong>?
            </p>
            <p className="text-sm text-gray-400 mt-2">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="delete-confirm-btn"
            >
              <Trash2 size={16} className="mr-2" />
              {deleting ? "Deleting..." : "Delete Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignDetail;